// Maps server-side master exercise identifiers to the PNG asset kind defined
// in exerciseAvatar.js. Lookup is forgiving:
//   1. explicit tracpatchConfig.assetKind on the master (preferred, future-proof)
//   2. exact name match (case-insensitive, trimmed)
//   3. substring fallback for naming drift
// Returns null when no PNG set should be used → caller renders the legacy
// KneeROMFigure SVG instead.

const NAME_TO_KIND = {
  // Exact-name entry so 'sitting lift 2' resolves to its own PNG set instead of
  // drifting onto SITTING_LIFT via the substring fallback (which matches the
  // 'sitting lift' prefix).
  'sitting lift 2':   'SITTING_LIFT_2',
  'sitting lift':     'SITTING_LIFT',
  // 'sit to stand 2' must precede both unsuffixed keys — the substring
  // fallback walks these in insertion order and would otherwise swallow a
  // suffixed name (e.g. 'Sit to Stand 2 (Left)') onto SIT_TO_STAND.
  // Kept in lockstep with the patient app's map.
  'sit to stand 2':   'SIT_TO_STAND_2',
  'sit-to-stand 2':   'SIT_TO_STAND_2',
  'sit to stand':     'SIT_TO_STAND',
  'sit-to-stand':     'SIT_TO_STAND',
  // ── Seated Heel Slide family ──────────────────────────────────────────────
  // Both seated entries are listed BEFORE 'heel slide' so the substring
  // fallback below can't swallow a suffixed name (e.g. 'Seated Heel Slide 2
  // (Left)') into the supine HEEL_SLIDE linear-bar set — the loop walks these
  // keys in insertion order. '…slide 2' precedes '…slide' for the same reason.
  //
  // The two variants ride DIFFERENT artwork (see the folder split in
  // exerciseAvatar.js): Seated Heel Slide 2 (exerciseId 9) gets the refreshed
  // HeelSlideROM frames, the original Seated Heel Slide (exerciseId 7) keeps
  // the older HeelSlideROM_v1 set. Exercise 9 also carries an explicit
  // tracpatchConfig.assetKind, which wins over any name match; the entry here
  // is the belt-and-braces path. Kept in lockstep with the patient app's map.
  'seated heel slide 2': 'HEEL_SLIDE_ROM_2',
  'seated heel slide': 'HEEL_SLIDE_ROM_V1',
  // ── Supine Heel Slide family ──────────────────────────────────────────────
  // '…slide 2' is listed BEFORE 'heel slide' so the substring fallback can't
  // swallow a suffixed name (e.g. 'Heel Slide 2 (Left)') onto the original
  // frames — the loop walks these keys in insertion order. Both SEATED keys
  // above already precede it, so 'Seated Heel Slide 2' still resolves to the
  // seated set. Heel Slide 2 also carries an explicit tracpatchConfig.assetKind,
  // which wins over any name match; the entry here is the belt-and-braces path.
  // Kept in lockstep with the patient app's map.
  'heel slide 2':     'HEEL_SLIDE_2',
  'heel slide':       'HEEL_SLIDE',
  'heel slide rom':   'HEEL_SLIDE_ROM',
  'heel slide r.o.m': 'HEEL_SLIDE_ROM',
  // ── Knee to Chest family ──────────────────────────────────────────────────
  // '…chest 2' is listed BEFORE 'knee to chest' so the substring fallback
  // can't swallow a suffixed name (e.g. 'Knee to Chest 2 (Left)') onto the
  // original frames — the loop walks these keys in insertion order. Knee to
  // Chest 2 also carries an explicit tracpatchConfig.assetKind, which wins over
  // any name match; the entry here is the belt-and-braces path.
  // Kept in lockstep with the patient app's map.
  'knee to chest 2':  'KNEE_TO_CHEST_2',
  'knee to chest':    'KNEE_TO_CHEST',
  // ── Lying Knee Flexion family ─────────────────────────────────────────────
  // '…flexion 2' is listed BEFORE '…flexion' so the substring fallback can't
  // swallow a suffixed name (e.g. 'Lying Knee Flexion 2 (Left)') onto the
  // original frames — the loop walks these keys in insertion order. Lying Knee
  // Flexion 2 also carries an explicit tracpatchConfig.assetKind, which wins
  // over any name match; the entry here is the belt-and-braces path.
  // Kept in lockstep with the patient app's map.
  'lying knee flexion 2': 'LYING_KNEE_FLEXION_2',
  'lying knee flexion': 'LYING_KNEE_FLEXION',
  'knee flexion':     'KNEE_FLEXION',
  // ── Short Arc Quad family ─────────────────────────────────────────────────
  // '…quad 2' is listed BEFORE '…quad' so the substring fallback can't swallow
  // a suffixed name (e.g. 'Short Arc Quad 2 (Left)') onto the original frames —
  // the loop walks these keys in insertion order.
  //
  // The two variants ride SEPARATE folders (ShortArcQuad2/ vs ShortArcQuad/,
  // see exerciseAvatar.js), so unlike the heel-slide split neither one had to
  // be migrated off shared art: the original Short Arc Quad (exerciseId 6)
  // stores no assetKind and keeps resolving here by name. Short Arc Quad 2
  // (exerciseId 11) also carries an explicit tracpatchConfig.assetKind, which
  // wins over any name match; the entry here is the belt-and-braces path.
  // Kept in lockstep with the patient app's map.
  'short arc quad 2': 'SHORT_ARC_QUAD_2',
  'short arc quad':   'SHORT_ARC_QUAD',
  // Mini Squat carries an explicit tracpatchConfig.assetKind, which wins over
  // any name match; the entry here is the belt-and-braces path. The standard
  // (non-TracPatch) 'Mini Squats' / 'Mini Squat with Chair' exercises would
  // match it by substring, but only TracPatch masters are ever resolved here.
  // Kept in lockstep with the patient app's map.
  'mini squat':       'MINI_SQUAT',
}

export function assetKindForExercise(master) {
  if (!master) return null
  if (master.tracpatchConfig && master.tracpatchConfig.assetKind) {
    return master.tracpatchConfig.assetKind
  }
  const name = ((master.name || '') + '').trim().toLowerCase()
  if (NAME_TO_KIND[name]) return NAME_TO_KIND[name]
  for (const key of Object.keys(NAME_TO_KIND)) {
    if (name.indexOf(key) !== -1) return NAME_TO_KIND[key]
  }
  return null
}
